"use client";

import { useState, useRef, useEffect } from "react";

export type Amenity = {
  key: string;
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
};

type Props = {
  amenitiesData: Amenity[];
  activeTab: string;
  setActiveTab: (k: string) => void;
};

export default function AmenitiesNav({
  amenitiesData,
  activeTab,
  setActiveTab,
}: Props) {
  // mobile “Quick links” accordion state
  const [open, setOpen] = useState(false);

  // measure content for smooth mobile height animation
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLUListElement | null>(null);
  const [contentH, setContentH] = useState(0);

  // recompute height on mount, data change, open/close, and resize
  useEffect(() => {
    const measure = () => {
      if (innerRef.current) setContentH(innerRef.current.scrollHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [amenitiesData, open]);

  // optional: close on tab change (mobile) to reveal chosen content
  useEffect(() => {
    setOpen(false);
  }, [activeTab]);

  return (
    <nav
      className="
        rounded-[25px]
        bg-[#ECE5DF] sm:bg-[#ffffff00]
        p-4 sm:p-6
        sm:sticky sm:top-[150px]
      "
      aria-label="Amenities navigation">
      {/* Mobile header / toggler */}
      <button
        className="
          w-full sm:hidden
          flex items-center justify-between
          rounded-[14px]
          px-4 py-3
          bg-white/70
          font-medium mt-0
        "
        aria-expanded={open}
        aria-controls="amenities-quicklinks"
        onClick={() => setOpen((s) => !s)}>
        <span>Quick links</span>
        <svg
          className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Collapsible wrapper */}
      <div
        id="amenities-quicklinks"
        ref={wrapRef}
        className="
          overflow-hidden
          transition-[max-height] duration-300 ease-in-out
          sm:max-h-full! sm:mt-0 sm:overflow-visible
          mt-3
        "
        style={{
          // Mobile: animate max-height from 0 -> measured height.
          // Desktop (sm:): class forces max-height: none (auto).
          maxHeight: open ? contentH : 0,
        }}>
        <ul role="tablist"
          ref={innerRef}
          className="flex sm:flex-col flex-wrap gap-2 sm:gap-3 mt-1">
          {amenitiesData.map((item) => {
            const isActive = activeTab === item.key;
            return (
              <li key={item.key} className="w-full" role="presentation">
                <button role="tab"
                  onClick={() => setActiveTab(item.key)}
                  className={`
                    block w-full text-left px-4 py-2 rounded-[12px]
                    transition-all cursor-pointer
                    ${isActive
                      ? "bg-[#e2d8cb] text-black font-semibold"
                      : "text-gray-700 hover:bg-[#ddd]"
                    }
                  `}
                  aria-selected={isActive}
                >
                  {item.title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Desktop title (optional) */}
      <div className="hidden sm:block mt-2 text-[16px] leading-[22px] text-[#6b6b6b]"></div>
    </nav>
  );
}
